import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import api from '../lib/api';
import { speak, stopSpeaking, createRecognizer, recognitionSupported } from '../lib/useVoice';
import { Mic, MicOff, PhoneOff, Loader2 } from 'lucide-react';

// Full-screen phone-style voice call with an agent. Loop: listen -> send -> speak -> listen.
export default function CallScreen({ chat, agent, onClose, onExchange }) {
  const [status, setStatus] = useState('connecting'); // connecting|listening|thinking|speaking|muted
  const [caption, setCaption] = useState('');
  const [muted, setMuted] = useState(false);
  const recRef = useRef(null);
  const activeRef = useRef(true);
  const mutedRef = useRef(false);

  const startListening = () => {
    if (!activeRef.current || mutedRef.current) return;
    const rec = createRecognizer({
      continuous: false,
      onInterim: (t) => setCaption(t),
      onResult: (finalText) => { if (finalText) handleUtterance(finalText); },
      onEnd: () => {
        // if still listening state and not muted, restart to keep the call open
        if (activeRef.current && !mutedRef.current && status === 'listening') {
          try { rec.start(); } catch (e) {}
        }
      },
    });
    recRef.current = rec;
    if (rec) { setStatus('listening'); try { rec.start(); } catch (e) {} }
  };

  const stopListening = () => { try { recRef.current && recRef.current.stop(); } catch (e) {} };

  const handleUtterance = async (text) => {
    stopListening();
    setStatus('thinking');
    setCaption(text);
    try {
      const res = await api.post(`/chats/${chat.chat_id}/messages`, { content: text });
      const reply = res.data.replies?.[0]?.content || "I'm here.";
      onExchange && onExchange();
      setStatus('speaking');
      setCaption(reply);
      speak(reply, { voice: agent?.voice, rate: agent?.voice_rate, onEnd: () => { if (activeRef.current && !mutedRef.current) startListening(); } });
    } catch (e) {
      const msg = e?.response?.status === 402 ? "You're out of credits. Upgrade to keep talking." : "Sorry, I hit a snag.";
      setStatus('speaking');
      setCaption(msg);
      speak(msg, { voice: agent?.voice, rate: agent?.voice_rate, onEnd: () => { if (activeRef.current && !mutedRef.current && e?.response?.status !== 402) startListening(); } });
    }
  };

  useEffect(() => {
    activeRef.current = true;
    const greeting = `Hi, you're on a call with ${agent?.name || 'your agent'}. What's on your mind?`;
    setStatus('speaking'); setCaption(greeting);
    const t = setTimeout(() => speak(greeting, { voice: agent?.voice, rate: agent?.voice_rate, onEnd: () => { if (activeRef.current && !mutedRef.current) startListening(); } }), 400);
    return () => { activeRef.current = false; clearTimeout(t); stopSpeaking(); stopListening(); };
    // eslint-disable-next-line
  }, []);

  const toggleMute = () => {
    const next = !muted; setMuted(next); mutedRef.current = next;
    if (next) { stopListening(); stopSpeaking(); setStatus('muted'); }
    else startListening();
  };

  const end = () => { activeRef.current = false; stopSpeaking(); stopListening(); onClose(); };

  const label = { connecting: 'Connecting…', listening: 'Listening…', thinking: 'Thinking…', speaking: 'Speaking…', muted: 'Muted' }[status];
  const noSupport = !recognitionSupported();

  return (
    <div className="fixed inset-0 z-[100] flex flex-col items-center justify-between py-16 px-6" style={{ background: 'hsl(var(--foreground))' }} data-testid="call-screen">
      <div className="text-center text-white/70">
        <p className="text-sm">On a call with</p>
        <p className="font-display text-2xl font-700 text-white">{agent?.name || 'Agent'}</p>
        <p className="text-xs mt-1">{label}</p>
      </div>

      <div className="flex flex-col items-center gap-6">
        <motion.div animate={status === 'speaking' ? { scale: [1, 1.08, 1] } : status === 'listening' ? { scale: [1, 1.04, 1] } : { scale: 1 }} transition={{ repeat: Infinity, duration: 1.4 }}
          className="h-40 w-40 rounded-full grid place-items-center text-6xl" style={{ background: (agent?.color || '#e8590c') + '33', boxShadow: `0 0 80px ${(agent?.color || '#e8590c')}55` }}>
          {agent?.avatar || '🤖'}
        </motion.div>
        <div className="min-h-[3rem] max-w-md text-center text-white/90 text-sm px-4">{caption}</div>
        {noSupport && <p className="text-amber-300 text-xs max-w-xs text-center">Voice needs a Chromium browser (Chrome/Edge). You can still type in chat.</p>}
      </div>

      <div className="flex items-center gap-6">
        <button onClick={toggleMute} className="h-14 w-14 rounded-full grid place-items-center text-white" style={{ background: muted ? 'hsl(var(--warning))' : 'rgba(255,255,255,0.15)' }} data-testid="call-mute-button">
          {muted ? <MicOff className="h-6 w-6" /> : <Mic className="h-6 w-6" />}
        </button>
        <button onClick={end} className="h-16 w-16 rounded-full grid place-items-center text-white" style={{ background: 'hsl(var(--destructive))' }} data-testid="call-end-button">
          <PhoneOff className="h-7 w-7" />
        </button>
        <div className="h-14 w-14 grid place-items-center">{status === 'thinking' && <Loader2 className="h-6 w-6 animate-spin text-white/70" />}</div>
      </div>
    </div>
  );
}
